import { useEffect, useRef, useState } from 'react';
import { Sheet } from './sheet.js';

/**
 * One question, one optional note field — the replacement for every `window.prompt`
 * that collected a movement or resolve note (DESIGN.md §4.1, 0.1.266).
 *
 * The prompt's contract survives the swap, deliberately: Close means cancel (nothing
 * is written), and an EMPTY field is a real answer — the caller receives `undefined`
 * and proceeds without a note, exactly as `prompt() === ''` did. The write itself
 * stays in the caller, on the same `call` timeout/classification path as before.
 *
 * Wears the shared bottom-sheet chrome (`<Sheet>`, S2 — the overlay rebuild): grabber,
 * scrim, 14px top corners, safe-area foot. One sheet look per era.
 */
export function NoteSheet({
  title,
  fieldLabel,
  confirmLabel,
  busy = false,
  onConfirm,
  onClose,
}: {
  title: string;
  fieldLabel: string;
  confirmLabel: string;
  busy?: boolean;
  /** `undefined` = confirmed with no note — the empty prompt's meaning, kept. */
  onConfirm: (note: string | undefined) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    fieldRef.current?.focus();
  }, []);
  return (
    <Sheet label={title} onClose={onClose} className="note-sheet">
      <header className="sheet-header">
        <strong>{title}</strong>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </header>
      <p className="field">
        <label>
          {fieldLabel}
          <textarea
            ref={fieldRef}
            rows={2}
            value={note}
            disabled={busy}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      </p>
      <p>
        <button
          type="button"
          className="btn-primary"
          disabled={busy}
          onClick={() => onConfirm(note.trim() === '' ? undefined : note.trim())}
        >
          {confirmLabel}
        </button>
      </p>
    </Sheet>
  );
}
